import React, { useState, useCallback } from 'react'
import { Box, Button, TextField } from '@material-ui/core'
import { useStyles } from './styles'
import {
  SortOrder,
  VIEW_MODES,
  POWER_LABEL,
  ENERGY_LABEL,
  POWER_MAP_KEY,
  ENERGY_MAP_KEY,
  POWER_TOTAL_KEY,
  ENERGY_TOTAL_KEY,
  POWER_UNIT,
  ENERGY_UNIT,
  POWER_UNIT_LABEL,
  ENERGY_UNIT_LABEL,
  REQUEST_BODY_PLACEHOLDER,
  YEARLY_REQUEST_BODY_PLACEHOLDER,
  PAGE_TITLE,
  CSV_HEADERS,
  CSV_FILE_PREFIX,
  GRAND_TOTAL_LABEL,
  TOTAL_COLOR_POWER,
  TOTAL_COLOR_ENERGY,
  DISCHARGE_EMPTY_TABLE_TEXT,
  MONTHS,
} from './constants'
import { getErrorMessage } from '../constants'
import TableData from '../TableData'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import { eventService } from '../../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../../utils/messageTypes'

const parseDateKey = (k) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(k)) return new Date(k).getTime()
  if (/^\d{4}-\d{2}$/.test(k)) return new Date(k + '-01').getTime()
  const m = k.match(/^([A-Za-z]{3})[- ](\d{2})$/)
  if (m) {
    const mon = MONTHS.findIndex(x => x.toLowerCase() === m[1].toLowerCase())
    return new Date(2000 + parseInt(m[2]), mon < 0 ? 0 : mon, 1).getTime()
  }
  return 0
}

const formatValue = (watts, unitLabel) =>
  (watts / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ` ${unitLabel}`

const escapeCsvValue = (v) => {
  const s = v == null ? '' : String(v)
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
}

const DailyDischargeViewer = (props) => {
  const classes = useStyles()

  const [allRows, setAllRows] = useState([])
  const [dateCols, setDateCols] = useState([])
  const [currentMode, setCurrentMode] = useState(VIEW_MODES.POWER)
  const [sortBy, setSortBy] = useState('siteId')
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC)
  const [searchTerm, setSearchTerm] = useState('')
  const [metaData, setMetaData] = useState({ serverTimeStamp: '', rowCount: 0 })
  const [reqBody, setReqBody] = useState(REQUEST_BODY_PLACEHOLDER)
  const [yearlyReqBody, setYearlyReqBody] = useState(YEARLY_REQUEST_BODY_PLACEHOLDER)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')

  const isPower = currentMode === VIEW_MODES.POWER
  const mapKey = isPower ? POWER_MAP_KEY : ENERGY_MAP_KEY
  const totalKey = isPower ? POWER_TOTAL_KEY : ENERGY_TOTAL_KEY
  const unitLabel = isPower ? POWER_UNIT_LABEL : ENERGY_UNIT_LABEL
  const rawUnit = isPower ? POWER_UNIT : ENERGY_UNIT
  const totalColor = isPower ? TOTAL_COLOR_POWER : TOTAL_COLOR_ENERGY

  const processResponse = useCallback((json) => {
    const meta = json.meta ?? {}
    setMetaData({
      serverTimeStamp: meta.serverTimeStamp ?? '',
      rowCount: meta.rowCount ?? (json.data?.length ?? 0),
    })

    const rows = json.data ?? []
    setAllRows(rows)

    const dateSet = new Set()
    rows.forEach(r => {
      Object.keys(r.avgDischargePowerMap ?? {}).forEach(d => { if (d !== POWER_TOTAL_KEY) dateSet.add(d) })
      Object.keys(r.totalDischargeEnergyMap ?? {}).forEach(d => { if (d !== ENERGY_TOTAL_KEY) dateSet.add(d) })
    })
    setDateCols([...dateSet].sort((a, b) => parseDateKey(a) - parseDateKey(b)))
    setSortBy('siteId')
    setSortOrder(SortOrder.ASC)
  }, [])

  const fetchDailyData = () => {
    let body
    try {
      body = JSON.parse(reqBody.trim())
    } catch (err) {
      setMessage('Invalid JSON in Request Body')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
      return
    }

    setLoading(true)
    eventService.getDailyDischargeSummary(body, props.currentUser.account_id)
      .then(data => {
        processResponse(data)
        setMessage(`${data.data?.length ?? 0} site(s) loaded`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to fetch daily discharge data'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const fetchYearlyData = () => {
    let body
    try {
      body = JSON.parse(yearlyReqBody.trim())
    } catch (err) {
      setMessage('Invalid JSON in Yearly Request Body')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
      return
    }

    setLoading(true)
    eventService.getYearlyDischargeSummary(body, props.currentUser.account_id)
      .then(data => {
        processResponse(data)
        setMessage(`Yearly summary: ${data.data?.length ?? 0} site(s) loaded`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to fetch yearly discharge data'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const getFilteredRows = useCallback(() => {
    const q = searchTerm.trim().toLowerCase()
    return q ? allRows.filter(r => String(r.siteId).includes(q)) : allRows
  }, [searchTerm, allRows])

  const getSortedRows = useCallback((rows) => {
    return [...rows].sort((a, b) => {
      let av, bv
      if (sortBy === 'siteId') {
        av = a.siteId
        bv = b.siteId
      } else {
        av = a[mapKey]?.[sortBy] ?? -1
        bv = b[mapKey]?.[sortBy] ?? -1
      }
      if (av < bv) return sortOrder === SortOrder.ASC ? -1 : 1
      if (av > bv) return sortOrder === SortOrder.ASC ? 1 : -1
      return 0
    })
  }, [sortBy, sortOrder, mapKey])

  const handleSortChange = (col, dir) => {
    setSortBy(col)
    setSortOrder(dir)
  }

  const exportCSV = (mode) => {
    if (!allRows.length) return
    const exportIsPower = mode === VIEW_MODES.POWER
    const exportMapKey = exportIsPower ? POWER_MAP_KEY : ENERGY_MAP_KEY
    const exportTotalKey = exportIsPower ? POWER_TOTAL_KEY : ENERGY_TOTAL_KEY
    const exportUnitLabel = exportIsPower ? POWER_UNIT_LABEL : ENERGY_UNIT_LABEL

    const rows = getFilteredRows()

    const dateSet = new Set()
    allRows.forEach(r => Object.keys(r[exportMapKey] ?? {}).forEach(d => { if (d !== exportTotalKey) dateSet.add(d) }))
    const cols = [...dateSet].sort((a, b) => parseDateKey(a) - parseDateKey(b))

    const header = [CSV_HEADERS.SITE_ID, CSV_HEADERS.PROGRAM_KEY, ...cols, `Total (${exportUnitLabel})`].map(escapeCsvValue).join(',')
    const body = rows.map(r => {
      const vals = cols.map(d => r[exportMapKey]?.[d] ?? '')
      return [r.siteId, r.programkey ?? '', ...vals,
      ((r[exportMapKey]?.[exportTotalKey] ?? 0) / 1000).toFixed(2)].map(escapeCsvValue).join(',')
    })
    const grandVals = cols.map(d => rows.reduce((s, r) => s + (r[exportMapKey]?.[d] ?? 0), 0))
    const grandTotal = rows.reduce((s, r) => s + (r[exportMapKey]?.[exportTotalKey] ?? 0), 0)
    const grandRow = [`${GRAND_TOTAL_LABEL} (${exportUnitLabel})`, '',
    ...grandVals.map(v => (v / 1000).toFixed(2)),
    (grandTotal / 1000).toFixed(2)].map(escapeCsvValue).join(',')

    const csv = [header, ...body, grandRow].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    const a = document.createElement('a')
    a.href = url
    a.download = `${CSV_FILE_PREFIX}-${mode}-${ts}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filteredRows = getFilteredRows()
  const sortedRows = getSortedRows(filteredRows)

  // Build grand total row
  const colTotals = {}
  sortedRows.forEach(r => {
    dateCols.forEach(d => {
      const v = r[mapKey]?.[d]
      if (v != null) colTotals[d] = (colTotals[d] ?? 0) + v
    })
  })
  const grandTotalValue = sortedRows.reduce((s, r) => s + (r[mapKey]?.[totalKey] ?? 0), 0)

  // Build tableHeadings for TableData
  const tableHeadings = [
    { id: 'siteId', label: CSV_HEADERS.SITE_ID, sortOrder: sortBy === 'siteId' ? sortOrder : SortOrder.ASC },
    { id: 'programkey', label: CSV_HEADERS.PROGRAM_KEY, sortOrder: null },
    ...dateCols.map(d => ({
      id: d,
      label: d,
      sortOrder: sortBy === d ? sortOrder : SortOrder.ASC,
    })),
    { id: 'total', label: `Total (${unitLabel})`, sortOrder: null },
  ]

  // Build tableContents: data rows + grand total row
  const tableContents = sortedRows.map(r => ({
    ...r,
    id: r.siteId,
    _isGrandTotal: false,
  }))
  if (sortedRows.length > 0) {
    tableContents.push({
      id: '__grand_total__',
      _isGrandTotal: true,
      _colTotals: colTotals,
      _grandTotal: grandTotalValue,
    })
  }

  const chipColorClass = isPower ? classes.chipOrange : classes.chipPurple
  const grandChipClass = isPower ? classes.grandChipPower : classes.grandChipEnergy
  const grandTotalClass = isPower ? classes.grandTotalPower : classes.grandTotalEnergy

  const renderCellContent = (item, heading) => {
    if (item._isGrandTotal) {
      if (heading.id === 'siteId') {
        return (
          <span className={`${classes.chip} ${grandChipClass}`}>
            {GRAND_TOTAL_LABEL} ({unitLabel})
          </span>
        )
      }
      if (heading.id === 'programkey') return ''
      if (heading.id === 'total') {
        return <strong className={grandTotalClass}>{formatValue(item._grandTotal, unitLabel)}</strong>
      }
      const t = item._colTotals[heading.id] ?? 0
      return <strong className={grandTotalClass}>{formatValue(t, unitLabel)}</strong>
    }

    switch (heading.id) {
      case 'siteId':
        return <span className={`${classes.chip} ${classes.chipBlue}`}>{item.siteId}</span>
      case 'programkey':
        return <span className={`${classes.chip} ${classes.chipDefault}`}>{item.programkey || '-'}</span>
      case 'total': {
        const rowTotal = (item[mapKey]?.[totalKey] ?? 0) / 1000
        return (
          <strong style={{ color: totalColor }}>
            {rowTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {unitLabel}
          </strong>
        )
      }
      default: {
        const v = item[mapKey]?.[heading.id]
        if (v == null) return <span className={`${classes.chip} ${classes.chipZero}`}>-</span>
        if (v === 0) return <span className={`${classes.chip} ${classes.chipZero}`}>0 {rawUnit}</span>
        return (
          <span className={`${classes.chip} ${chipColorClass}`}>
            {v.toLocaleString()} {rawUnit}
          </span>
        )
      }
    }
  }

  const getCellClassName = (item, heading) => {
    const cellClasses = [classes.titleWrapper]

    if (heading.id === 'total') {
      cellClasses.push(isPower ? classes.totalColPower : classes.totalColEnergy)
    }

    if (heading.id !== 'siteId' && heading.id !== 'programkey') {
      cellClasses.push(classes.numCell)
    }

    if (item._isGrandTotal) {
      cellClasses.push(isPower ? classes.grandRowCellPower : classes.grandRowCellEnergy)
    }

    return cellClasses.join(' ')
  }

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlExtraGroup}>
          <Box className={classes.urlBarLabel}>
            <Box>Request Body <Box component="span" className={classes.labelSubtext}>(JSON object - Daily)</Box></Box>
            <Button className={classes.submitBtn} onClick={fetchDailyData} disabled={loading}>
              <Search />
              <Box paddingLeft='10px'>Fetch & Render</Box>
            </Button>
          </Box>
          <TextField
            className={classes.reqTextarea}
            value={reqBody}
            onChange={(e) => setReqBody(e.target.value)}
            multiline
            variant="standard"
            InputProps={{ disableUnderline: true, style: { color: '#e2e8f0', padding: '8px 12px' } }}
          />
        </Box>

        <Box className={classes.urlExtraGroup} style={{ marginTop: '10px' }}>
          <Box className={classes.urlBarLabel}>
            <Box>Request Body <Box component="span" className={classes.labelSubtext}>(JSON object - Yearly Summary)</Box></Box>
            <Button className={classes.yearlyBtn} onClick={fetchYearlyData} disabled={loading}>
              <Search />
              <Box paddingLeft='10px'>Fetch Yearly Summary</Box>
            </Button>
          </Box>
          <TextField
            className={classes.reqTextarea}
            value={yearlyReqBody}
            onChange={(e) => setYearlyReqBody(e.target.value)}
            multiline
            variant="standard"
            InputProps={{ disableUnderline: true, style: { color: '#e2e8f0', padding: '8px 12px' } }}
          />
        </Box>
      </Box>

      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box className={classes.sectionTitle}>{PAGE_TITLE}</Box>
          <Box className={classes.metaPills}>
            <Box component="span" className={classes.metaPill}>
              <Box component="span" className={classes.metaPillStrong}>Server Time:</Box> {metaData.serverTimeStamp || '-'}
            </Box>
            <Box component="span" className={classes.metaPill}>
              <Box component="span" className={classes.metaPillStrong}>Row Count:</Box> {metaData.rowCount}
            </Box>
          </Box>
        </Box>

        <Box className={classes.filterBar}>
          <TextField
            className={classes.filterInput}
            placeholder="Search Site ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Button className={classes.clearBtn} onClick={() => setSearchTerm('')}>Clear</Button>

          <Box className={classes.toggleGroup}>
            <Button
              className={`${classes.toggleBtn} ${isPower ? classes.toggleBtnActive : ''}`}
              onClick={() => setCurrentMode(VIEW_MODES.POWER)}
            >
              Power
            </Button>
            <Button
              className={`${classes.toggleBtn} ${classes.toggleBtnBorder} ${!isPower ? classes.toggleBtnActive : ''}`}
              onClick={() => setCurrentMode(VIEW_MODES.ENERGY)}
            >
              Energy
            </Button>
          </Box>
          <Box component="span" className={isPower ? classes.modeBannerPower : classes.modeBannerEnergy}>
            {isPower ? POWER_LABEL : ENERGY_LABEL}
          </Box>

          <Box component="span" className={classes.resultCount}>
            {filteredRows.length} of {allRows.length} site{allRows.length !== 1 ? 's' : ''}
          </Box>

          <Button className={classes.exportPowerBtn} onClick={() => exportCSV(VIEW_MODES.POWER)}>
            <Download />
            <Box paddingLeft='5px'>Export Power CSV</Box>
          </Button>
          <Button className={classes.exportEnergyBtn} onClick={() => exportCSV(VIEW_MODES.ENERGY)}>
            <Download />
            <Box paddingLeft='5px'>Export Energy CSV</Box>
          </Button>
        </Box>

        <TableData
          classes={classes}
          tableHeadings={tableHeadings}
          tableContents={tableContents}
          isBold={sortBy}
          handleSortChange={handleSortChange}
          renderCellContent={renderCellContent}
          getCellClassName={getCellClassName}
          containerClassName={classes.tableWrap}
          tableClassName={classes.dischargeTable}
          stickyHeader={false}
          emptyTableText={DISCHARGE_EMPTY_TABLE_TEXT}
        />
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(DailyDischargeViewer)
